import { DialogComponent } from '@theme/dialog';
import { announceCartLinesUpdate } from '@theme/cart-events';
import { addBundleToCart } from '@theme/bundle-add-to-cart';
import { fetchConfig } from '@theme/utilities';

/**
 * Quick Shop analytics. Emitted twice — a DOM CustomEvent on `document` for listener-based tooling
 * and a `dataLayer` push for GTM — matching the convention the cart modules use.
 *
 * @param {string} eventName
 * @param {Record<string, unknown>} [detail]
 */
function pushQuickShopAnalytics(eventName, detail = {}) {
  document.dispatchEvent(new CustomEvent(eventName, { detail }));

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: eventName, ...detail });
}

/**
 * Spelled-out size names for the group label ("Size: Extra Small") while the pills keep the short
 * code. Mirrors snippets/size-name.liquid, which renders the same expansion server-side — both
 * lists have to move together. Anything unlisted passes through unchanged.
 * @type {Record<string, string>}
 */
const SIZE_NAMES = {
  XXS: '2X Small',
  XS: 'Extra Small',
  S: 'Small',
  M: 'Medium',
  L: 'Large',
  XL: 'Extra Large',
  XXL: '2X Large',
  XXXL: '3X Large',
  YXS: 'Youth Extra Small',
  YS: 'Youth Small',
  YM: 'Youth Medium',
  YL: 'Youth Large',
  YXL: 'Youth Extra Large',
};

const SELECT_SIZE = 'Select Size';

/**
 * Size guide markup, once per product handle, kept across opens so a second click is instant.
 * @type {Map<string, Element>}
 */
const sizeGuideCache = new Map();

/**
 * Product URL honouring a localized root (`/en-ca/products/...`).
 * @param {string} handle
 * @returns {string}
 */
function productPath(handle) {
  const root = (Theme?.routes?.root_url ?? '/').replace(/\/$/, '');
  return `${root}/products/${encodeURIComponent(handle)}`;
}

/**
 * In-flight and completed body fetches, keyed `handle::variantId`.
 *
 * The body is a full section render — on a 26-colour product that is ~160KB and ~800ms of server
 * time — so the shopper would otherwise watch a skeleton for a second on every open. `prefetch()`
 * starts the same request on hover/touch intent and this map hands the pending promise to the
 * click, which usually lands after the response.
 * @type {Map<string, Promise<string>>}
 */
const bodyCache = new Map();

/**
 * GOAT (perf/correctness): the body cache is bounded and invalidated on cart changes.
 *
 * Each entry is ~160KB of HTML for a many-colour product, and the hover warm adds one per card, so
 * a shopper scrolling a long collection accumulated megabytes of retained strings for the life of
 * the page — memory pressure and GC pauses land squarely in the INP measurement. It was also never
 * invalidated, so after adding the last unit of a size the modal would still show it in stock.
 */
const BODY_CACHE_LIMIT = 8;

/**
 * @param {string} key
 * @param {Promise<string>} pending
 */
function cacheBody(key, pending) {
  // Map iterates in insertion order, so the first key is the least recently added.
  while (bodyCache.size >= BODY_CACHE_LIMIT) {
    const oldest = bodyCache.keys().next().value;
    if (oldest === undefined) break;
    bodyCache.delete(oldest);
  }
  bodyCache.set(key, pending);
}

document.addEventListener('shopify:cart:lines-update', () => bodyCache.clear());

/**
 * One body per product; the card's variant is applied client-side in `loadAndShow`.
 * @param {string} handle
 * @returns {Promise<string>}
 */
function fetchQuickShopBody(handle) {
  const key = handle;
  const hit = bodyCache.get(key);
  if (hit) return hit;

  const url = `${productPath(handle)}?section_id=quick-shop-product`;

  const pending = fetch(url)
    .then((response) => {
      if (!response.ok) throw new Error(`Quick Shop section fetch failed: ${response.status}`);
      return response.text();
    })
    .catch((error) => {
      // A failed fetch must not be cached, or the card stays broken until reload.
      bodyCache.delete(key);
      throw error;
    });

  cacheBody(key, pending);
  return pending;
}

/**
 * Warms the body for a product the shopper is only hovering. Errors are swallowed: nothing has
 * been asked for yet, and the click path reports its own failure.
 * @param {string} handle
 */
export function prefetchQuickShop(handle) {
  if (!handle) return;
  fetchQuickShopBody(handle).catch(() => {});
}

/**
 * Comma-joined section ids of every cart line-items section on the page, so the `/cart/add.js`
 * response carries their re-rendered HTML. Same contract as product-form.js.
 * @returns {string}
 */
function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
    .filter(Boolean)
    .join(',');
}

/**
 * Opens the cart drawer after a quick-shop add.
 *
 * Horizon's own auto-open (assets/cart-drawer.js) defers until the source dialog fires a native
 * `close`, which makes the drawer's appearance depend on a handshake between two independent
 * promise handlers. Doing it explicitly keeps it deterministic.
 *
 * The host lookup matches any dialog-component host, not one hardcoded tag: routing through
 * `closeDialog()` is what releases the scroll lock (`unlockScroll`). Falling through to a raw
 * `dialog.close()` would leave the page locked behind the drawer.
 *
 * @param {EventTarget} dispatcher
 */
function revealCartDrawer(dispatcher) {
  /** @type {(HTMLElement & { open?: () => void }) | null} */
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer || typeof drawer.open !== 'function') return;

  const sourceModal = dispatcher instanceof Element ? dispatcher.closest('dialog:modal') : null;

  if (sourceModal) {
    /** @type {(Element & { closeDialog?: () => void }) | null} */
    const host = sourceModal.closest('quick-shop-dialog, quick-add-dialog, dialog-component');

    if (host && typeof host.closeDialog === 'function') host.closeDialog();
    else sourceModal.close();
  }

  drawer.open();
}

/**
 * Adds a variant to the cart and announces it, so the cart drawer, the cart line items and the
 * cart bubble all react.
 *
 * @param {string} variantId
 * @param {EventTarget} dispatcher
 * @param {string} sourceId
 * @param {string} [sellingPlanId] - Adds the line on this plan.
 * @throws when the add fails, so callers can restore their own button state
 */
async function addVariantToCart(variantId, dispatcher, sourceId, sellingPlanId) {
  const body = new FormData();
  body.append('id', variantId);
  body.append('quantity', '1');
  if (sellingPlanId) body.append('selling_plan', sellingPlanId);

  const sections = cartSectionIds();
  if (sections) body.append('sections', sections);

  const config = fetchConfig('javascript', { body });
  const response = await fetch(Theme.routes.cart_add_url, {
    ...config,
    headers: { ...config.headers, Accept: 'text/html' },
  });

  if (!response.ok) throw new Error('Failed to add to cart');

  const result = await response.json();
  if (result.status) throw new Error(result.description || result.message || 'Failed to add to cart');

  await announceCartLinesUpdate({
    target: dispatcher,
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: variantId, quantity: 1 }],
    source: 'quick-shop',
    sourceId,
    sections: result.sections,
    variantId,
    // A single-item `/cart/add.js` responds with the line item itself (merged quantity included),
    // which is the shape `detail.items` consumers expect — so no extra cart read is needed.
    // Guarded on `variant_id`, the field those consumers match on.
    items: result.variant_id ? [result] : undefined,
  });

  revealCartDrawer(dispatcher);
}

/**
 * Wires the injected body: option selection, variant resolution, gallery, add to cart.
 *
 * Listeners are bound imperatively rather than declaratively because the body is rendered by a
 * section and injected into the shell's subtree — a Horizon `on:` attribute there would resolve to
 * <quick-shop-dialog>, not to this controller.
 */
class QuickShopBody {
  /** @param {HTMLElement} root - the `.qsp` element from sections/quick-shop-product.liquid */
  constructor(root) {
    this.root = root;
    this.abortController = new AbortController();

    this.handle = root.dataset.productHandle || '';
    this.productUrl = root.dataset.productUrl || '';
    this.productTitle = root.dataset.productTitle || '';
    this.ctaText = root.dataset.ctaText || 'Add to Bag';
    this.sellingPlanId = root.dataset.sellingPlanId || '';
    this.bundle = this.#readBundle();
    this.colorIndex = Number(root.dataset.colorOptionIndex ?? -1);
    this.sizeIndex = Number(root.dataset.sizeOptionIndex ?? -1);

    this.variants = this.#readVariants();
    this.selected = this.#readInitialSelection();

    this.addButton = root.querySelector('[data-qsp-add]');
    this.detailsLink = root.querySelector('[data-qsp-details]');
    this.priceEl = root.querySelector('[data-qsp-price]');
    this.compareEl = root.querySelector('[data-qsp-compare]');
    this.saveEl = root.querySelector('[data-qsp-save]');
    this.track = root.querySelector('[data-qsp-track]');
    this.slides = Array.from(root.querySelectorAll('[data-qsp-slide]'));
    this.thumbs = Array.from(root.querySelectorAll('[data-qsp-thumb]'));
    this.progressBar = root.querySelector('[data-qsp-progress-bar]');

    this.#bind();
    this.#syncSwatchToggle();
    this.#filterGalleryByColor();
    this.#update();
  }

  destroy() {
    this.abortController.abort();
  }

  /** @returns {Array<{id:number, available:boolean, options:string[], price:number, priceFormatted:string, compareAtPrice:number|null, compareAtPriceFormatted:string|null, mediaId:number|null}>} */
  #readVariants() {
    const script = this.root.querySelector('[data-qsp-variants]');
    if (!script?.textContent) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  /** @returns {import('./bundle-add-to-cart.js').BundleCart | null} */
  #readBundle() {
    const script = this.root.querySelector('script[data-bundle-cart]');
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /** @returns {string[]} the selected value per option index; '' for a size still to be chosen */
  #readInitialSelection() {
    const initialId = Number(this.root.dataset.selectedVariantId || 0);
    const initial =
      this.variants.find((v) => v.id === initialId) || this.variants.find((v) => v.available) || this.variants[0];
    const selected = initial ? [...initial.options] : [];

    // As on the PDP gate, a one-value size has nothing to choose and stays preselected.
    const sizeGroup = this.root.querySelector(`[data-qsp-group][data-option-index="${this.sizeIndex}"]`);
    if (this.sizeIndex >= 0 && (sizeGroup?.querySelectorAll('[data-qsp-pill]').length ?? 0) > 1) {
      selected[this.sizeIndex] = '';
    }

    return selected;
  }

  #sizePending() {
    return this.sizeIndex >= 0 && !this.selected[this.sizeIndex];
  }

  /**
   * @param {{options: string[]}} variant
   * @param {number} [skipIndex]
   */
  #fitsSelection(variant, skipIndex = -1) {
    return variant.options.every((opt, i) => i === skipIndex || !this.selected[i] || opt === this.selected[i]);
  }

  #bind() {
    const { signal } = this.abortController;

    for (const swatch of this.root.querySelectorAll('[data-qsp-swatch]')) {
      swatch.addEventListener(
        'click',
        () => {
          if (swatch.getAttribute('aria-disabled') === 'true') return;
          this.#select(this.colorIndex, swatch.dataset.color || '');
        },
        { signal }
      );
    }

    for (const pill of this.root.querySelectorAll('[data-qsp-pill]')) {
      const group = pill.closest('[data-qsp-group]');
      const index = Number(group?.dataset.optionIndex ?? -1);
      pill.addEventListener(
        'click',
        () => {
          if (pill.classList.contains('is-unavailable')) return;
          this.#select(index, pill.dataset.value || '');
        },
        { signal }
      );
    }

    const toggle = this.root.querySelector('[data-qsp-swatch-toggle]');
    const grid = this.root.querySelector('[data-qsp-swatch-grid]');
    if (toggle && grid) {
      toggle.addEventListener(
        'click',
        () => {
          const expanded = grid.classList.toggle('is-expanded');
          toggle.textContent = expanded ? 'See fewer colors' : 'See more colors';
        },
        { signal }
      );
    }

    for (const thumb of this.thumbs) {
      thumb.addEventListener('click', () => this.#setActiveMedia(Number(thumb.dataset.galleryIndex) || 0), {
        signal,
      });
    }

    if (this.track && this.progressBar) {
      this.updateProgress = () => {
        const scrollable = this.track.scrollWidth - this.track.clientWidth;
        const ratio = scrollable > 0 ? this.track.scrollLeft / scrollable : 0;
        // Never a zero-width bar: at rest the thumb should still read as "one of N".
        const visible = this.slides.filter((s) => !s.hidden).length || 1;
        const base = 100 / visible;
        this.progressBar.style.width = `${Math.min(100, base + ratio * (100 - base))}%`;
      };
      this.track.addEventListener('scroll', this.updateProgress, { passive: true, signal });
      window.addEventListener('resize', this.updateProgress, { signal });
      this.updateProgress();
    }

    this.addButton?.addEventListener('click', () => this.#addToCart(), { signal });

    this.root.querySelector('[data-qsp-size-guide]')?.addEventListener('click', () => this.#openSizeGuide(), {
      signal,
    });
  }

  /**
   * @param {number} optionIndex
   * @param {string} value
   */
  #select(optionIndex, value) {
    if (optionIndex < 0) return;
    this.selected[optionIndex] = value;

    // A sold-out colour keeps the chosen size; the CTA reads "Sold out" until the shopper changes it.
    if (optionIndex === this.colorIndex) this.#filterGalleryByColor();

    this.#update();
  }

  /** @returns {object|undefined} */
  #currentVariant() {
    return this.variants.find((v) => v.options.every((opt, i) => opt === this.selected[i]));
  }

  /** @returns {object|undefined} the selected variant, or while the size is pending the colour's first available one */
  #displayVariant() {
    const exact = this.#currentVariant();
    if (exact || !this.#sizePending()) return exact;

    const candidates = this.variants.filter((v) => this.#fitsSelection(v));
    return candidates.find((v) => v.available) || candidates[0];
  }

  #update() {
    const variant = this.#displayVariant();

    for (const swatch of this.root.querySelectorAll('[data-qsp-swatch]')) {
      const color = swatch.dataset.color || '';
      const isSelected = color === this.selected[this.colorIndex];
      swatch.classList.toggle('is-selected', isSelected);
      swatch.setAttribute('aria-pressed', String(isSelected));

      // Sold out in every size is server-rendered and blocked; this strikes colours missing in the selected size.
      if (swatch.getAttribute('aria-disabled') === 'true') continue;
      const buyable = this.variants.some(
        (v) => v.available && v.options[this.colorIndex] === color && this.#fitsSelection(v, this.colorIndex)
      );
      swatch.classList.toggle('is-size-unavailable', !buyable);
      swatch.setAttribute('aria-label', buyable ? color : `${color} - Out of stock`);
    }

    for (const pill of this.root.querySelectorAll('[data-qsp-pill]')) {
      const group = pill.closest('[data-qsp-group]');
      const index = Number(group?.dataset.optionIndex ?? -1);
      const value = pill.dataset.value || '';
      const isSelected = this.selected[index] === value;
      pill.classList.toggle('is-selected', isSelected);
      pill.setAttribute('aria-pressed', String(isSelected));

      // A size with no buyable variant for the current colour is disabled, never removed — the row
      // keeps its length so it does not reflow as the shopper moves between colourways.
      const buyable = this.variants.some(
        (v) => v.available && v.options[index] === value && this.#fitsSelection(v, index)
      );
      pill.classList.toggle('is-unavailable', !buyable);
      pill.setAttribute('aria-disabled', String(!buyable));
    }

    for (const group of this.root.querySelectorAll('[data-qsp-group]')) {
      const index = Number(group.dataset.optionIndex ?? -1);
      const valueEl = group.querySelector('[data-qsp-option-value]');
      if (!valueEl || index < 0) continue;

      const raw = this.selected[index] || '';
      valueEl.textContent =
        valueEl.hasAttribute('data-qsp-expand-size') ? SIZE_NAMES[raw.trim().toUpperCase()] ?? raw : raw;
    }

    if (variant) {
      if (this.priceEl) this.priceEl.textContent = variant.priceFormatted;

      const onSale = variant.compareAtPrice != null && variant.compareAtPrice > variant.price;
      this.priceEl?.classList.toggle('qsp__price--sale', onSale);

      if (this.compareEl) {
        this.compareEl.textContent = onSale ? variant.compareAtPriceFormatted || '' : '';
        this.compareEl.hidden = !onSale;
      }

      if (this.saveEl) {
        const percent = onSale ? Math.round(((variant.compareAtPrice - variant.price) / variant.compareAtPrice) * 100) : 0;
        this.saveEl.textContent = percent > 0 ? `Save ${percent}%` : '';
        this.saveEl.hidden = percent <= 0;
      }

      if (this.detailsLink instanceof HTMLAnchorElement) {
        this.detailsLink.href = `${this.productUrl}?variant=${variant.id}`;
      }
    }

    if (this.addButton instanceof HTMLButtonElement) {
      const buyable = Boolean(variant?.available);
      // PDP gate parity: "Select Size" keeps its enabled look and the click does nothing.
      const pending = buyable && this.#sizePending();
      this.addButton.disabled = !buyable;
      this.addButton.textContent = pending ? SELECT_SIZE : buyable ? this.ctaText : 'Sold out';
      if (pending) this.addButton.setAttribute('aria-disabled', 'true');
      else this.addButton.removeAttribute('aria-disabled');
    }
  }

  /** Hide the "See more colors" toggle unless the grid actually overflows its collapsed height. */
  #syncSwatchToggle() {
    const toggle = this.root.querySelector('[data-qsp-swatch-toggle]');
    const grid = this.root.querySelector('[data-qsp-swatch-grid]');
    if (!toggle || !grid) return;

    requestAnimationFrame(() => {
      toggle.hidden = grid.scrollHeight <= grid.clientHeight + 1;
    });
  }

  /**
   * Mirror the PDP gallery (snippets/product-media-gallery-content.liquid): the selected variant's
   * image first, then — in admin order — media tagged with the selected colourway plus untagged
   * (shared) media. Everything shows when that leaves nothing.
   *
   * Media carry the colour as a trailing `#color_<handle>` token in alt text; the section stamps
   * the normalised key onto each slide and swatch as `data-color-code`.
   */
  #filterGalleryByColor() {
    if (!this.slides.length) return;

    const color = this.selected[this.colorIndex];
    const code = color ? this.#colorCode(color) : '';
    const leadId = String(this.#leadMediaId(color) ?? '');

    let shown = this.slides.map(
      (slide) =>
        slide.dataset.mediaId === leadId || !code || !slide.dataset.colorCode || slide.dataset.colorCode === code
    );
    if (!shown.includes(true)) shown = shown.map(() => true);

    const lead = this.slides.findIndex((slide, i) => shown[i] && slide.dataset.mediaId === leadId);
    this.#orderMedia(lead);

    this.slides.forEach((slide, i) => {
      slide.hidden = !shown[i];
      if (this.thumbs[i]) this.thumbs[i].hidden = !shown[i];
    });

    const active = lead >= 0 ? lead : shown.indexOf(true);
    if (active >= 0) this.#setActiveMedia(active);
    this.updateProgress?.();
  }

  /** @param {string} color */
  #colorCode(color) {
    const swatch = Array.from(this.root.querySelectorAll('[data-qsp-swatch]')).find((s) => s.dataset.color === color);
    return swatch?.dataset.colorCode ?? '';
  }

  /** @param {string|undefined} color */
  #leadMediaId(color) {
    const current = this.#displayVariant();
    if (current?.mediaId && (this.colorIndex < 0 || current.options[this.colorIndex] === color)) return current.mediaId;
    return this.variants.find((v) => v.mediaId && v.options[this.colorIndex] === color)?.mediaId ?? null;
  }

  /**
   * Restore admin order, then move the lead slide (and its thumb) to the front.
   * @param {number} lead - index into this.slides, or -1
   */
  #orderMedia(lead) {
    const thumbList = this.thumbs[0]?.parentElement;
    this.track?.append(...this.slides);
    thumbList?.append(...this.thumbs);
    if (lead < 0) return;
    this.track?.prepend(this.slides[lead]);
    if (this.thumbs[lead]) thumbList?.prepend(this.thumbs[lead]);
  }

  /** @param {number} index */
  #setActiveMedia(index) {
    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    this.thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === index));

    const target = this.slides[index];
    if (target && this.track && !target.hidden && this.track.clientWidth < this.track.scrollWidth) {
      this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
    }
  }

  /**
   * Opens the size guide for this product.
   *
   * The guide is fetched once per handle and appended to <body>, not into the modal: dialog.js's
   * keydown handler calls preventDefault() without stopPropagation(), so a nested guide would take
   * the modal down with it on a single Escape. As siblings in the top layer the later showModal()
   * wins, Escape reaches only the guide, and the scroll lock — which is owner-set — stays held by
   * the modal underneath.
   */
  async #openSizeGuide() {
    const existing = sizeGuideCache.get(this.handle);
    if (existing?.isConnected) {
      existing.showDialog?.();
      return;
    }

    try {
      const response = await fetch(`${productPath(this.handle)}?section_id=quick-shop-size-guide`);
      if (!response.ok) throw new Error(`Size guide fetch failed: ${response.status}`);

      const holder = document.createElement('div');
      holder.innerHTML = await response.text();

      const guide = holder.querySelector('dialog-component');
      if (!guide) throw new Error('Size guide markup missing its dialog-component');

      // Only ever one guide in the document. The section's id is fixed (`size-guide-quick-shop-
      // size-guide`, see sections/quick-shop-size-guide.liquid) and its tab inputs are id/`for`
      // pairs, so a second product's guide would duplicate every one of those ids and the tabs
      // would drive the first copy. Costs one re-fetch when a shopper alternates between two
      // products, which the cache still covers for the common reopen-the-same-product case.
      for (const [handle, cached] of sizeGuideCache) {
        if (handle !== this.handle) {
          cached.remove();
          sizeGuideCache.delete(handle);
        }
      }

      // Appending connects it, which is what upgrades the custom element; wait for the definition
      // first so showDialog() is guaranteed to exist on the instance.
      document.body.appendChild(guide);
      await customElements.whenDefined('dialog-component');

      sizeGuideCache.set(this.handle, guide);
      guide.showDialog?.();
    } catch (error) {
      console.error('[quick-shop] size guide:', error);
    }
  }

  async #addToCart() {
    const variant = this.#currentVariant();
    if (!variant?.available || !(this.addButton instanceof HTMLButtonElement)) return;

    const original = this.addButton.textContent;
    this.addButton.disabled = true;
    this.addButton.textContent = 'Adding…';

    try {
      if (this.bundle) {
        await addBundleToCart(this.bundle, { sourceId: `quick-shop-${this.handle}`, productId: this.root.dataset.productId });
        revealCartDrawer(this.addButton);
      } else {
        await addVariantToCart(String(variant.id), this.addButton, `quick-shop-${this.handle}`, this.sellingPlanId);
      }

      pushQuickShopAnalytics('quick_shop_add_to_bag', {
        product_handle: this.handle,
        product_title: this.productTitle,
        variant_id: variant.id,
      });
    } catch (error) {
      console.error('[quick-shop] add to cart failed:', error);
      this.addButton.disabled = false;
      this.addButton.textContent = original;
      this.#showError('Sorry, we could not add that to your bag. Please try again.');
    }
  }

  /** @param {string} message */
  #showError(message) {
    let slot = this.root.querySelector('[data-qsp-error]');
    if (!slot) {
      slot = document.createElement('p');
      slot.className = 'qsp__error';
      slot.setAttribute('data-qsp-error', '');
      slot.setAttribute('role', 'alert');
      this.root.querySelector('.qsp__cta')?.prepend(slot);
    }
    slot.textContent = message;
    slot.hidden = false;
  }
}

/**
 * The global shell. Subclasses DialogComponent so the top layer, the scroll lock, `::backdrop`,
 * Escape, outside-click and focus restore all come from Horizon rather than being hand-rolled —
 * which is what the retired in-card overlay had to do, because a panel nested in a product card
 * cannot be promoted to the top layer.
 */
class QuickShopDialog extends DialogComponent {
  requiredRefs = ['dialog'];

  /** @type {QuickShopBody|null} */
  #body = null;
  /** @type {string|null} */
  #contentKey = null;
  /** @type {Element|null} */
  #card = null;
  /** @type {HTMLElement|null} */
  #trigger = null;
  #loading = false;
  /** Generation guard: a slow fetch must not paint over a newer one. */
  #token = 0;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('dialog:after-close', this.#handleAfterClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('dialog:after-close', this.#handleAfterClose);
    this.#destroyBody();
  }

  #handleAfterClose = () => {
    pushQuickShopAnalytics('quick_shop_close', { product_handle: this.#contentKey?.split('::')[0] ?? null });
    this.#trigger?.setAttribute('aria-expanded', 'false');
  };

  #destroyBody() {
    this.#body?.destroy();
    this.#body = null;
  }

  /**
   * Opens the dialog and parks focus on the panel.
   *
   * `showModal()` focuses the first focusable descendant, which is the close button, and its
   * :focus-visible ring draws a box around the X that production does not have (GOAT-387). The
   * `autofocus` attribute on the dialog is not honoured for this — it is only consulted on
   * descendants — so the move is made here. The dialog carries tabindex="-1" to receive it.
   */
  #show() {
    this.showDialog();

    // Deferred a frame: showModal()'s own focusing steps run after this call stack and would
    // otherwise land on the close button again, re-drawing the ring.
    requestAnimationFrame(() => this.refs.dialog?.focus({ preventScroll: true }));
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const { loader, body, errorSlot } = this.refs;
    if (isLoading && errorSlot) errorSlot.hidden = true;
    if (loader) loader.hidden = !isLoading;
    if (body) body.hidden = isLoading;
  }

  #setError(hasError) {
    const { errorSlot, body, loader } = this.refs;
    if (errorSlot) errorSlot.hidden = !hasError;
    if (body) body.hidden = hasError;
    if (hasError && loader) loader.hidden = true;
  }

  /**
   * Fetch a product's body and open the modal.
   *
   * @param {string} handle
   * @param {Element} card - the originating product card
   * @param {string|null} [variantId] - variant selected on the card
   * @param {HTMLElement|null} [trigger] - element that opened the modal
   */
  async loadAndShow(handle, card, variantId = null, trigger = null) {
    const contentKey = `${handle}::${variantId || ''}`;

    // `instanceof Element`, not a truthiness check: a caller that passed anything else (a stale
    // `event.currentTarget`, which is the delegating document in WebKit) threw here, and the
    // caller's catch reads that as "quick shop unavailable" and opens Horizon's old quick-add
    // modal instead. A bad trigger must cost the focus return, not the whole panel.
    if (trigger instanceof Element) {
      this.#trigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
    }

    // Reuse injected markup only when the SAME card reopens: a handle-only cache would wire one
    // card's body to another card's swatch selection.
    if (this.#card === card && this.#contentKey === contentKey && this.#body) {
      this.#show();
      pushQuickShopAnalytics('quick_shop_open', { product_handle: handle });
      return;
    }

    /*
     * GOAT: a click on a second card while the first was still loading used to be swallowed
     * entirely — no dialog, no spinner, nothing — which reads as the feature being broken. The
     * generation token below already makes superseding safe: the earlier response is discarded on
     * `token !== this.#token`, and its in-flight fetch still lands in `bodyCache` as a warm.
     */
    this.#loading = true;
    this.#card = card;
    this.#contentKey = contentKey;

    const token = ++this.#token;

    this.#destroyBody();
    if (this.refs.body) this.refs.body.innerHTML = '';
    this.#setError(false);
    this.#setLoading(true);
    this.#show();
    pushQuickShopAnalytics('quick_shop_open', { product_handle: handle });

    try {
      const html = await fetchQuickShopBody(handle);

      // The shopper reopened a different card while this was in flight; that open owns the panel.
      if (token !== this.#token) return;

      this.#setLoading(false);
      if (this.refs.body) {
        this.refs.body.innerHTML = html;
        const root = this.refs.body.querySelector('[data-quick-shop-product]');
        if (root instanceof HTMLElement) {
          if (variantId) root.dataset.selectedVariantId = variantId;
          this.#body = new QuickShopBody(root);
        }
      }
    } catch (error) {
      if (token !== this.#token) return;
      console.error('[quick-shop]', error);
      this.#contentKey = null;
      this.#setLoading(false);
      this.#setError(true);
    } finally {
      if (token === this.#token) this.#loading = false;
    }
  }

  /**
   * @param {string} handle
   * @param {Element} card
   * @param {string|null} [variantId]
   * @param {HTMLElement|null} [trigger]
   */
  static async trigger(handle, card, variantId = null, trigger = null) {
    const modal = document.getElementById('quick-shop-modal');
    if (!(modal instanceof QuickShopDialog)) return false;
    await modal.loadAndShow(handle, card, variantId, trigger);
    return true;
  }
}

if (!customElements.get('quick-shop-dialog')) {
  customElements.define('quick-shop-dialog', QuickShopDialog);
}

export { QuickShopDialog, addVariantToCart, revealCartDrawer, pushQuickShopAnalytics };
